import { useState, useEffect } from 'react';
import { Gift, Copy, Users, CheckCircle, Clock, Share2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';

export default function BrokerReferrals() {
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setError(null);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) return;
      
      const response = await api.get(`/broker/referrals/${user.id}`);
      setReferralCode(response.data.referralCode || '');
      setReferrals(response.data.referrals || []);
    } catch (err) {
      console.error('Error fetching referral data:', err);
      setError('Failed to load referral data.');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/broker/auth?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const shareOnWhatsApp = () => {
    const link = `${window.location.origin}/broker/auth?ref=${referralCode}`;
    const message = `Join ALTX as a partner broker and earn up to 2.5% commission on every investment! Use my referral code: ${referralCode}\n\nSign up here: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <BrokerSidebar />
      
      <main className="md:ml-64 p-6">
        <div className="max-w-4xl mx-auto">
          {error && <ErrorBanner message={error} onRetry={fetchReferralData} />}
          {loading ? <DashboardSkeleton cards={3} rows={3} /> : (<>
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl text-white font-light mb-2">Refer Brokers</h1>
            <p className="text-white/50">Invite other brokers to join ALTX and earn rewards</p>
          </div>

          {/* Referral Code Card */}
          <div className="bg-gradient-to-r from-[#4ADE80]/10 to-transparent border border-[#4ADE80]/30 rounded-xl p-6 mb-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5 text-[#4ADE80]" />
                  <span className="text-sm text-white/60">Your Referral Code</span>
                </div>
                <div className="flex items-center gap-4">
                  <code className="text-3xl font-mono text-[#4ADE80]">{referralCode || '---'}</code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyReferralCode}
                    className="border-[#4ADE80]/30 text-[#4ADE80] hover:bg-[#4ADE80]/10"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyReferralLink}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <Link2 className="w-4 h-4 mr-1" />
                  Copy Link
                </Button>
                <Button
                  size="sm"
                  onClick={shareOnWhatsApp}
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#4ADE80]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#4ADE80]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-white">{referrals.length}</p>
                  <p className="text-xs text-white/50">Total Referrals</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#4ADE80]/10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#4ADE80]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-white">
                    {referrals.filter(r => r.approvedAt).length}
                  </p>
                  <p className="text-xs text-white/50">Approved</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-white">Coming Soon</p>
                  <p className="text-xs text-white/50">Rewards Earned</p>
                </div>
              </div>
            </div>
          </div>

          {/* How it works */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
            <h3 className="text-lg text-white mb-4">How it works</h3>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#4ADE80]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#4ADE80] font-semibold">1</span>
                </div>
                <h4 className="text-white font-medium mb-1">Share Your Code</h4>
                <p className="text-sm text-white/50">Share your referral code with other real estate brokers</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#4ADE80]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#4ADE80] font-semibold">2</span>
                </div>
                <h4 className="text-white font-medium mb-1">They Sign Up</h4>
                <p className="text-sm text-white/50">They register using your code and complete KYC</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#4ADE80]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#4ADE80] font-semibold">3</span>
                </div>
                <h4 className="text-white font-medium mb-1">Earn Rewards</h4>
                <p className="text-sm text-white/50">Once approved, you receive referral rewards</p>
              </div>
            </div>
          </div>

          {/* Referrals List */}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white font-medium">Your Referrals</h3>
            </div>
            
            {loading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-[#4ADE80] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white/50">Loading referrals...</p>
              </div>
            ) : referrals.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No referrals yet</p>
                <p className="text-sm text-white/30 mt-1">Share your referral code to get started</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {referrals.map((referral, index) => (
                  <div key={index} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#4ADE80]/20 rounded-full flex items-center justify-center">
                        <span className="text-[#4ADE80] font-medium">
                          {referral.brokerName?.charAt(0) || 'B'}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{referral.brokerName}</p>
                        <p className="text-xs text-white/50">
                          Joined {new Date(referral.approvedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-[#4ADE80]/10 text-[#4ADE80] text-xs rounded-full">
                        Approved
                      </span>
                      {referral.rewardAmount && (
                        <span className="text-[#4ADE80] font-medium">
                          +₹{referral.rewardAmount.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>)}
        </div>
      </main>
    </div>
  );
}
